import { useEffect, useState } from "react";
import { Package, Check, CreditCard, Repeat } from "lucide-react";
import { getCatalog, createCheckout } from "@/api/entities";
import { Panel, Label, Loading, Pill, HudButton, accent } from "@/components/hud";
import { useOperator } from "@/context/OperatorContext";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

export default function Catalog() {
  const { canWrite } = useOperator();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getCatalog().then((d) => setItems(d.items || d || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const buy = async (it) => {
    setBusy(it.id);
    try {
      const res = await createCheckout({ item_id: it.id, origin: window.location.origin });
      if (res?.url) {
        window.location.assign(res.url);
      } else {
        toast.error("Checkout could not be started");
        setBusy(null);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 503) {
        toast.error("Payments are offline — add a Stripe key in Settings to accept real payments.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Checkout needs operator sign-in");
      }
      setBusy(null);
    }
  };

  if (loading) return <Loading label="Loading service catalog…" />;

  return (
    <div className="space-y-6 yb-rise" data-testid="catalog-page">
      <div>
        <Label>Catalog · productised services</Label>
        <h1 className="font-display text-[26px] sm:text-[32px] mt-1.5" style={{ color: "var(--yb-text)" }}>
          Service Catalog
        </h1>
        <p className="text-[13px] mt-2 max-w-2xl" style={{ color: "var(--yb-text-muted)" }}>
          Fixed-scope offers for Melbourne SMBs. Prices are list prices — actual revenue only
          appears in Treasury once a client has paid through Stripe.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const tone = it.featured ? "magenta" : "cyan";
          const { c } = accent(tone);
          return (
            <Panel key={it.id} className="flex flex-col gap-4" glow={it.featured}>
              <div className="flex items-start justify-between gap-2">
                <span className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ border: `1px solid ${c}`, color: c }}>
                  <Package size={18} />
                </span>
                {it.featured && <Pill tone="magenta">Most popular</Pill>}
                {it.recurring && !it.featured && <Pill tone="mint">Recurring</Pill>}
              </div>
              <div>
                <h3 className="font-head text-[17px]" style={{ color: "var(--yb-text)" }}>{it.name}</h3>
                <div className="flex items-baseline gap-1 mt-1.5">
                  <span className="font-mono text-[26px]" style={{ color: c }}>
                    {fmtMoney(it.price_cents, { currency: it.currency })}
                  </span>
                  <span className="font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
                    {it.recurring ? "/mo" : "one-off"}
                  </span>
                </div>
              </div>
              {it.summary && (
                <p className="text-[12.5px] leading-relaxed" style={{ color: "var(--yb-text-dim)" }}>{it.summary}</p>
              )}
              {Array.isArray(it.features) && it.features.length > 0 && (
                <ul className="space-y-1.5 pt-2" style={{ borderTop: "1px solid var(--yb-hairline)" }}>
                  {it.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px]" style={{ color: "var(--yb-text-dim)" }}>
                      <Check size={13} style={{ color: c, marginTop: 2 }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              {canWrite && (
                <HudButton tone={tone} onClick={() => buy(it)} disabled={busy === it.id}
                  className="mt-auto w-full" data-testid={`catalog-buy-${it.id}`}>
                  {it.recurring ? <Repeat size={14} /> : <CreditCard size={14} />}
                  {busy === it.id ? "Opening checkout…" : it.recurring ? "Subscribe" : "Purchase"}
                </HudButton>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
